import FileDownloadIcon from '@mui/icons-material/FileDownload'; // Add download icon import
import FileUploadIcon from '@mui/icons-material/FileUpload';
import HomeIcon from '@mui/icons-material/Home';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import {
    Box,
    Button,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { vtt2srt } from '../../../libs/readSub/SubUtils'; // Import the conversion utility
import { formatSaveType } from '../../../utils/formatUtils';
import { getLanguagesForEditView } from '../SelectLanguageComponent';

const HeaderContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    color: white;

    .header-title {
        font-weight: bold;
        font-size: 1.2rem;
        white-space: nowrap;
        margin-right: 16px;
    }

    .video-info {
        display: flex;
        flex-direction: column;
        margin-right: 16px;
        min-width: 200px;
    }

    .video-name {
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 300px;
    }

    .breadcrumb-container {
        font-size: 0.8rem;
    }

    .column-controls {
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        gap: 8px;
    }

    .column-control {
        min-width: 180px;
        flex: 1;
    }

    .column-header {
        display: flex;
        align-items: center;
        margin-bottom: 4px;
    }

    .controls-row {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .lang-select {
        flex: 0.6;
    }

    .version-select {
        flex: 1;
    }

    .right-controls {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .import-button,
    .download-button {
        min-width: 32px;
        width: 32px;
        height: 32px;
        padding: 0;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

// Define the colors for consistency
const columnColors = [
    '#4caf50', // Green - Column 1
    '#2196f3', // Blue - Column 2
    '#ff9800', // Orange - Column 3
    '#9c27b0', // Purple - Column 4
    '#e91e63', // Pink - Column 5
];

// Helper function to convert hex to RGB for background opacity
function getColorRGB(hex) {
    // Remove # if present
    hex = hex.replace('#', '');

    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return `${r}, ${g}, ${b}`;
}

export default function MultiColumnHeader({
    columns,
    handleLanguageChange,
    handleVersionChange,
    handleImportFile,
    timelineExpanded,
    toggleTimeline,
    headerExpanded,
    toggleHeader,
    getSubtitleContent, // Add prop to get subtitle content for download
}) {
    const navigate = useNavigate();
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [videoInfo, setVideoInfo] = useState({
        name: '',
        folderPath: '',
    });

    // Create refs for file inputs (one for each column)
    const fileInputRefs = useRef([]);

    // Initialize refs for file inputs
    useEffect(() => {
        fileInputRefs.current = Array(columns.length)
            .fill()
            .map((_, i) => fileInputRefs.current[i] || React.createRef());
    }, [columns.length]);

    useEffect(() => {
        // Get available languages from the API or helper function
        const languages = getLanguagesForEditView();
        setAvailableLanguages(languages);

        // Get video info from localStorage
        try {
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));

            if (videoProps) {
                setVideoInfo({
                    name: videoProps.videoName || 'Untitled Video',
                });
            }
        } catch (error) {
            console.error('Error loading video information:', error);
        }
    }, []);

    // Handle file selection
    const handleFileSelect = (index, event) => {
        const file = event.target.files[0];
        if (file && columns[index].language) {
            // Clear the file input for future selections
            event.target.value = '';

            // Call the parent component's handler
            if (handleImportFile) {
                handleImportFile(index, file, columns[index].language);
            }
        }
    };

    // Handle download SRT file
    const handleDownloadSRT = async (index) => {
        // Ensure column has a language selected
        if (!columns[index].language) return;

        try {
            // Get subtitle content (VTT format) from parent component
            const vttContent = await getSubtitleContent(index);

            if (!vttContent) {
                console.error('No subtitle content available for download');
                return;
            }

            // Convert VTT to SRT format
            const srtContent = vtt2srt(vttContent);

            // Create download
            const blob = new Blob([srtContent], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            // Generate filename: VideoName_Language_version.srt
            const fileName = `${videoInfo.name}_${columns[index].language}_version${columns[index].version}.srt`;
            a.download = fileName;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading SRT file:', error);
        }
    };

    return (
        <HeaderContainer>
            <div className="video-info">
                <Typography className="video-name" variant="subtitle1">
                    <VideoFileIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    {videoInfo.name}
                </Typography>
            </div>

            <div className="column-controls">
                {columns.map((column, index) => {
                    // Get the color for this column
                    const columnColor = columnColors[index] || '#ccc';

                    return (
                        <Box
                            key={index}
                            className="column-control"
                            sx={{
                                borderTop: `2px solid ${columnColor}`,
                                borderLeft: `1px solid ${columnColor}`,
                                borderRight: `1px solid ${columnColor}`,
                                borderBottom: `1px solid ${columnColor}`,
                                borderRadius: '4px',
                                padding: '6px',
                                backgroundColor: `rgba(${getColorRGB(columnColor)}, 0.05)`,
                            }}
                        >
                            <div className="column-header">
                                <Typography
                                    variant="caption"
                                    style={{
                                        color: columnColor,
                                        fontWeight: 'bold',
                                        marginRight: '8px',
                                    }}
                                >
                                    Column {index + 1}
                                </Typography>
                            </div>

                            <div className="controls-row">
                                <FormControl size="small" variant="outlined" className="lang-select">
                                    <InputLabel id={`language-select-${index}`} style={{ color: columnColor }}>
                                        Language
                                    </InputLabel>
                                    <Select
                                        labelId={`language-select-${index}`}
                                        value={column.language || ''}
                                        onChange={(e) => handleLanguageChange(index, e.target.value)}
                                        label="Language"
                                        sx={{
                                            color: 'white',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                borderColor: `${columnColor}50`,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: columnColor,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: columnColor,
                                            },
                                            '.MuiSvgIcon-root': {
                                                color: columnColor,
                                            },
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {availableLanguages.map((lang) => (
                                            <MenuItem
                                                key={lang}
                                                value={lang}
                                                style={{
                                                    borderLeft: `3px solid ${columnColor}`,
                                                    backgroundColor:
                                                        column.language === lang
                                                            ? `rgba(${getColorRGB(columnColor)}, 0.1)`
                                                            : 'transparent',
                                                }}
                                            >
                                                {lang}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl
                                    size="small"
                                    variant="outlined"
                                    disabled={!column.language || column.versions?.length === 0}
                                    className="version-select"
                                >
                                    <InputLabel id={`version-select-${index}`} style={{ color: columnColor }}>
                                        Version
                                    </InputLabel>
                                    <Select
                                        labelId={`version-select-${index}`}
                                        value={column.version}
                                        onChange={(e) => handleVersionChange(index, e.target.value)}
                                        label="Version"
                                        sx={{
                                            color: 'white',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                borderColor: `${columnColor}50`,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: columnColor,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: columnColor,
                                            },
                                            '.MuiSvgIcon-root': {
                                                color: columnColor,
                                            },
                                        }}
                                        endAdornment={
                                            <>
                                                {/* Download button */}
                                                <Tooltip title={`Download ${column.language || 'subtitles'} as SRT`}>
                                                    <IconButton
                                                        className="download-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (column.language) {
                                                                handleDownloadSRT(index);
                                                            }
                                                        }}
                                                        disabled={!column.language}
                                                        size="small"
                                                        sx={{
                                                            color: columnColor,
                                                            mr: 0.5,
                                                            opacity: column.language ? 1 : 0.5,
                                                        }}
                                                    >
                                                        <FileDownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* Import button */}
                                                <Tooltip title={`Import ${column.language || 'subtitles'}`}>
                                                    <IconButton
                                                        className="import-button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (column.language) {
                                                                fileInputRefs.current[index].current.click();
                                                            }
                                                        }}
                                                        disabled={!column.language}
                                                        size="small"
                                                        sx={{
                                                            color: columnColor,
                                                            mr: 1,
                                                            opacity: column.language ? 1 : 0.5,
                                                        }}
                                                    >
                                                        <FileUploadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        }
                                    >
                                        <MenuItem
                                            value="latest"
                                            style={{
                                                borderLeft: `3px solid ${columnColor}`,
                                            }}
                                        >
                                            Latest
                                        </MenuItem>

                                        {/* Add a menu item for imported version */}
                                        {column.importedFile && (
                                            <MenuItem
                                                value="imported"
                                                style={{
                                                    borderLeft: `3px solid ${columnColor}`,
                                                    backgroundColor:
                                                        column.version === 'imported'
                                                            ? `rgba(${getColorRGB(columnColor)}, 0.1)`
                                                            : 'transparent',
                                                }}
                                            >
                                                Imported: {column.importedFile.split('/').pop()}
                                            </MenuItem>
                                        )}

                                        {column.versions?.map((version) => (
                                            <MenuItem
                                                key={version.id}
                                                value={version.id}
                                                style={{
                                                    borderLeft: `3px solid ${columnColor}`,
                                                    backgroundColor:
                                                        column.version === version.id
                                                            ? `rgba(${getColorRGB(columnColor)}, 0.1)`
                                                            : 'transparent',
                                                }}
                                            >
                                                {`V${version.versionNumber}: ${formatSaveType(version.saveType)}`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {/* Hidden file input for each column */}
                                <input
                                    type="file"
                                    ref={fileInputRefs.current[index]}
                                    style={{ display: 'none' }}
                                    accept=".srt,.vtt,.ass,.json"
                                    onChange={(e) => handleFileSelect(index, e)}
                                />
                            </div>
                        </Box>
                    );
                })}
            </div>

            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)', mx: 2 }} />

            <div className="right-controls">
                <IconButton color="inherit" onClick={() => navigate('/')} title="Home">
                    <HomeIcon />
                </IconButton>

                {/* Timeline toggle button */}
                <Tooltip title={timelineExpanded ? 'Hide Timeline' : 'Show Timeline'}>
                    <Button
                        onClick={toggleTimeline}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={timelineExpanded ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                        sx={{
                            ml: 1,
                            fontSize: '0.75rem',
                            padding: '4px 8px',
                        }}
                    >
                        {timelineExpanded ? 'Hide Timeline' : 'Show Timeline'}
                    </Button>
                </Tooltip>

                {/* Header toggle button */}
                <Tooltip title="Hide Header">
                    <Button
                        onClick={toggleHeader}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<KeyboardArrowUp />}
                        sx={{
                            ml: 1,
                            fontSize: '0.75rem',
                            padding: '4px 8px',
                        }}
                    >
                        Hide Header
                    </Button>
                </Tooltip>
            </div>
        </HeaderContainer>
    );
}
